import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import {
  Phone, MessageCircle, ArrowLeft, Plus, Wallet, IndianRupee,
  TrendingUp, Scale, Receipt, CalendarClock, FileText,
} from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

function inr(n) {
  return `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [pay, setPay] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setData(r.data));
    api.get(`/payments/customer/${id}`).then((r) => setPay(r.data)).catch(() => setPay(null));
  }, [id]);

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const { customer, orders } = data;
  const totalWeight = orders.reduce((a, o) => a + (o.final_weight || o.estimated_weight || 0), 0);

  return (
    <div className="space-y-6">
      <Link to="/customers" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Customers
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500">Customer</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">{customer.name}</h1>
          <p className="text-zinc-500 mt-1">{customer.shop_name || "—"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`tel:${customer.primary_phone}`}>
            <Button variant="outline" data-testid="customer-call"><Phone className="w-4 h-4 mr-1.5" /> Call</Button>
          </a>
          <a href={`https://wa.me/${customer.primary_phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="text-emerald-600" data-testid="customer-whatsapp">
              <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
            </Button>
          </a>
          <Link to={`/orders/new?customer_id=${customer.id}`}>
            <Button className="bg-zinc-900 hover:bg-zinc-800"><Plus className="w-4 h-4 mr-1.5" /> Order</Button>
          </Link>
          <Button variant="outline" data-testid="statement-pdf-btn" onClick={async () => {
            try {
              const r = await api.get(`/customers/${customer.id}/statement-pdf`, { responseType: "blob" });
              const blob = new Blob([r.data], { type: "application/pdf" });
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank");
            } catch { toast.error("Could not open statement PDF"); }
          }}>
            <FileText className="w-4 h-4 mr-1.5" /> Statement PDF
          </Button>
        </div>
      </div>

      {/* Summary stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><Receipt className="w-3 h-3" />Orders</div>
          <div className="font-heading font-black text-2xl mt-1 num">{orders.length}</div>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><Scale className="w-3 h-3" />Weight</div>
          <div className="font-heading font-black text-2xl mt-1 num">{totalWeight.toFixed(2)}<span className="text-sm text-zinc-500 ml-1">g</span></div>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" />Total</div>
          <div className="font-heading font-black text-2xl mt-1 num">{pay ? inr(pay.summary.total_amount) : "—"}</div>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><Wallet className="w-3 h-3" />Paid</div>
          <div className="font-heading font-black text-2xl mt-1 num text-emerald-700">{pay ? inr(pay.summary.total_paid) : "—"}</div>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Balance</div>
          <div className="font-heading font-black text-2xl mt-1 num text-red-700">{pay ? inr(pay.summary.outstanding) : "—"}</div>
        </Card>
        <Card className="p-4">
          <div className="overline text-zinc-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" />Next Due</div>
          <div className="font-heading font-bold text-sm mt-2">
            {pay?.summary?.upcoming_due
              ? new Date(pay.summary.upcoming_due.due_date).toLocaleDateString()
              : "—"}
          </div>
        </Card>
      </div>

      {/* Opening Balance + pending metals */}
      {pay && (pay.opening_balance || pay.summary.pending_gold > 0 || pay.summary.pending_silver > 0) && (
        <Card className="p-6 space-y-4" data-testid="opening-balance-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-heading font-bold text-lg tracking-tight">Opening Balance & Pending Metal</h3>
            {pay.summary.total_amount > 0 && (
              <div className="text-xs text-zinc-500">
                Collection Progress: <span className="font-semibold text-zinc-900 num">{pay.summary.collection_progress}%</span>
              </div>
            )}
          </div>
          {pay.summary.total_amount > 0 && (
            <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, pay.summary.collection_progress)}%` }} />
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm break-words">
            {pay.opening_balance && (
              <>
                <div>
                  <div className="overline text-zinc-500">Opening Amount</div>
                  <div className="font-heading font-black text-xl num">{inr(pay.opening_balance.amount)}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                    {pay.opening_balance.outstanding_type === "payable" ? "We pay customer" : "Customer pays us"}
                  </div>
                </div>
                <div>
                  <div className="overline text-zinc-500">Opening Metal</div>
                  <div className="font-heading font-black text-xl">
                    {pay.opening_balance.metal_type || "—"}
                    {pay.opening_balance.pending_weight > 0 && (
                      <span className="text-sm text-zinc-500 ml-1 num">{pay.opening_balance.pending_weight}g</span>
                    )}
                  </div>
                  {pay.opening_balance.date && (
                    <div className="text-[10px] text-zinc-500">Since {new Date(pay.opening_balance.date).toLocaleDateString()}</div>
                  )}
                </div>
              </>
            )}
            <div>
              <div className="overline text-zinc-500">Pending Gold</div>
              <div className="font-heading font-black text-xl num">{(pay.summary.pending_gold || 0).toFixed(3)}<span className="text-sm text-zinc-500 ml-1">g</span></div>
            </div>
            <div>
              <div className="overline text-zinc-500">Pending Silver</div>
              <div className="font-heading font-black text-xl num">{(pay.summary.pending_silver || 0).toFixed(3)}<span className="text-sm text-zinc-500 ml-1">g</span></div>
            </div>
          </div>
          {pay.opening_balance?.remarks && (
            <div className="text-xs text-zinc-500 bg-zinc-50 border border-border rounded p-3">
              <span className="font-semibold text-zinc-700">Remarks:</span> {pay.opening_balance.remarks}
            </div>
          )}
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 space-y-3">
          <div>
            <div className="overline text-zinc-500">Primary Phone</div>
            <div className="num text-sm mt-1">{customer.primary_phone}</div>
          </div>
          {customer.alternate_phones?.length > 0 && (
            <div>
              <div className="overline text-zinc-500">Alternate Phones</div>
              <div className="space-y-1 mt-1">
                {customer.alternate_phones.map((p, i) => (
                  <div key={i} className="num text-sm">{p}</div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="overline text-zinc-500">Address</div>
            <div className="text-sm mt-1 text-zinc-700 whitespace-pre-wrap">{customer.address || "—"}</div>
          </div>
          {customer.notes && (
            <div>
              <div className="overline text-zinc-500">Notes</div>
              <div className="text-sm mt-1 text-zinc-700 whitespace-pre-wrap">{customer.notes}</div>
            </div>
          )}
        </Card>

        <Card className="md:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl">Order History ({orders.length})</h2>
          </div>
          {orders.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6">No orders for this customer yet.</div>
          ) : (
            <div className="divide-y divide-border" data-testid="customer-orders-list">
              {orders.map((o) => (
                <Link key={o.id} to={`/orders/${o.id}`} className="block py-3 hover:bg-zinc-50 -mx-2 px-2 rounded-md">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">{o.order_no}</span>
                    <StatusBadge status={o.status} />
                    <PriorityBadge priority={o.priority} />
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <div className="font-medium text-sm">{o.title}</div>
                    {o.expected_delivery && (
                      <div className="text-xs text-zinc-500">
                        Due {new Date(o.expected_delivery).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Payments */}
      {pay && pay.payments.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Payment Records ({pay.payments.length})
            </h2>
            <Link to="/payments" className="text-xs text-zinc-500 hover:text-zinc-900">All payments</Link>
          </div>
          <div className="divide-y divide-border">
            {pay.payments.map((p) => (
              <Link key={p.id} to={`/payments/${p.id}`} className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 rounded-md">
                <div>
                  <div className="font-medium text-sm">{p.product_name}</div>
                  <div className="text-xs text-zinc-500">
                    {p.status}{p.due_date ? ` · Due ${new Date(p.due_date).toLocaleDateString()}` : ""}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <div className="num font-bold text-red-700">{inr(p.remaining_balance)}</div>
                  <div className="text-xs text-zinc-500">of {inr(p.total_amount)}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Payment timeline */}
      {pay && pay.history.length > 0 && (
        <Card className="p-6">
          <h2 className="font-heading font-bold text-xl mb-4">Payment Timeline</h2>
          <ol className="relative border-l border-border ml-3 space-y-4">
            {pay.history.slice(0, 20).map((h) => (
              <li key={h.id} className="ml-6">
                <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-emerald-600 rounded-full border-2 border-white" />
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs text-zinc-500">{new Date(h.date).toLocaleString()}</div>
                    <div className="text-sm font-medium">{h.product_name} · {h.mode}</div>
                    {h.notes && <div className="text-xs text-zinc-600 mt-0.5">{h.notes}</div>}
                  </div>
                  <div className="num font-bold text-emerald-700">{inr(h.amount)}</div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
